import { filter, find, isEqual, uniq } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components/macro';
import { WxButton, WxLabel, WxRadioColor, WxSelect, WxText } from '../..';
import useAttributesByKey from '../../../hooks/api/queries/useAttributesByKey';
import { ImageType } from '../../../hooks/api/queries/useProducts';
import {
  VariantAttribute,
  VariantType,
} from '../../../hooks/api/queries/useVariantsByProductId';
import { __ } from '../../../i18n/l10n';
import { breakpoint } from '../../../styles/constants';
import { WImage } from '../../ui/image/Image';

interface Props {
  variants: VariantType[];
  setImageSelected: (index: number) => void;
  setVariantSelected: (id: number) => void;
  images: ImageType[];
}

function arraysEqual(a1: number[], a2: number[]) {
  return JSON.stringify(a1) === JSON.stringify(a2);
}

export const VariantPicker = React.memo(
  ({ variants, setVariantSelected, setImageSelected, images }: Props) => {
    // Get all sizes possible
    const [allSizes, setAllSizes] = useState<any[]>([]);
    const { data } = useAttributesByKey({ attributeKeyId: 5 });
    useEffect(() => {
      if (data) {
        const attributeValues = find(data, (d) => d.id === 5)?.values ?? [];
        setAllSizes(
          filter(attributeValues, (attr: any) =>
            ['XS', 'S', 'M', 'L', 'XL', 'XXL'].includes(attr.label),
          ),
        );
      }
    }, [data]);

    const [lastSelectedIndex, setLastSelectedIndex] = useState<number>(-1);

    const attributesByKeys = useMemo(() => {
      const attrFlat = variants.map((variant) => variant.attributes).flat();
      const uniqAttrValuesId = Array.from(
        new Set(attrFlat.map((attr) => attr?.id)),
      );
      const uniqAttrValues = uniqAttrValuesId.map(
        (id) => attrFlat.find((attr) => attr?.id === id) as VariantAttribute,
      );

      var uniqAttrKeys = uniqAttrValues.reduce(
        (accumulator: VariantAttribute[], current) => {
          if (checkIfAlreadyExist(current)) {
            return accumulator;
          } else {
            return [...accumulator, current];
          }

          function checkIfAlreadyExist(current: VariantAttribute) {
            return accumulator.some((item: any) => {
              return (
                item.keyId === current.keyId &&
                item.keyLabel === current.keyLabel
              );
            });
          }
        },
        [],
      );

      return uniqAttrKeys
        .sort((a, b) => {
          if (a.keySorting === null) return 1;
          else if (b.keySorting === null) return -1;
          else return a.keySorting - b.keySorting;
        })
        .map((key) => ({
          id: key.keyId,
          label: key.keyLabel,
          values: uniqAttrValues
            .filter((attr) => attr.keyId === key.keyId)
            .sort((a, b) => {
              if (a.valueSorting === null) return 1;
              else if (b.valueSorting === null) return -1;
              else return a.valueSorting - b.valueSorting;
            }),
        }));
    }, [variants]);

    const combinations = useMemo(
      () =>
        variants.map((variant) => ({
          quantity: variant.quantity,
          attributes: variant.attributes
            ?.sort(
              (a, b) =>
                attributesByKeys.findIndex((key) => key.id === a.keyId) -
                attributesByKeys.findIndex((key) => key.id === b.keyId),
            )
            .map((attr) => attr.id),
        })),
      [variants, attributesByKeys],
    );

    const [selected, setSelected] = useState<number[]>(
      attributesByKeys.map((_) => -1),
    );

    useEffect(() => {
      const index = combinations.findIndex((comb) => {
        return (
          comb.quantity > 0 && arraysEqual(comb.attributes ?? [], selected)
        );
      });

      if (index !== -1) setVariantSelected(index);
    }, [selected, combinations, setVariantSelected, lastSelectedIndex]);

    /** finds first variant image available */
    useEffect(() => {
      const filtered = selected.filter((n, i) => n !== -1);
      if (filtered.length === 0) setImageSelected(0);
      else {
        const variantMatching = variants.find(
          (e) =>
            filtered.every((f) => e.attributes?.map((e) => e.id).includes(f)) &&
            (e.localizedImage?.path || e.defaultImage?.path),
        );
        const image = variantMatching?.localizedImage?.path
          ? variantMatching.localizedImage
          : variantMatching?.defaultImage;

        const imageIndex = images.findIndex((e) => e.id === image?.id);
        setImageSelected(imageIndex === -1 ? 0 : imageIndex);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    //// Auto select next attribute if only one is available
    useEffect(() => {
      if (
        combinations[0].attributes &&
        lastSelectedIndex < combinations[0].attributes.length
      ) {
        const combArrayToTest = combinations.filter((comb) => {
          const comboSliced = comb.attributes?.slice(0, lastSelectedIndex + 1);
          const selectedSliced = selected.slice(0, lastSelectedIndex + 1);
          return isEqual(comboSliced, selectedSliced);
        });
        const valueArrayToTest = combArrayToTest.map((comb) => {
          if (comb.attributes) return comb.attributes[lastSelectedIndex + 1];
          else return -1;
        });

        if (uniq(valueArrayToTest).length === 1) {
          selected[lastSelectedIndex + 1] = valueArrayToTest[0];
          const newTemp = selected.map((t, i) => {
            if (i > lastSelectedIndex + 1) {
              setVariantSelected(-1);
              return -1;
            } else {
              return selected[i];
            }
          });

          const clearNewTemp = newTemp.filter((e) => e !== undefined);

          setSelected(clearNewTemp);
          setLastSelectedIndex(lastSelectedIndex + 1);
        }
      }
    }, [combinations, lastSelectedIndex, selected, setVariantSelected]);

    return (
      <Wrapper>
        <ul>
          {attributesByKeys.map((key, index) => {
            const values = key.values;

            if (key.id === 5) {
              for (let i = 0; i < allSizes.length; i++) {
                const value = allSizes[i];
                if (values.findIndex((v) => v.id === value.id) === -1) {
                  values.push(value);
                }
              }
            }
            // ;

            return (
              <Li
                key={key.id}
                $hidden={
                  index !== 0 && index > selected.filter((i) => i !== -1).length
                }
              >
                <WxLabel label={key.label} />
                <UlValues>
                  {values
                    .sort((a, b) => a.valueSorting - b.valueSorting)
                    .map((value, i) => {
                      /** simulate combination if clicked */
                      let temp = [...selected];
                      temp[index] = value.id;

                      // const filtered = selected.filter((n, i) => n !== -1);
                      const filteredTemp = temp.filter((n, i) => n !== -1);

                      const combArrayToTest = combinations.filter((comb) => {
                        const comboSliced = comb.attributes?.slice(
                          0,
                          index + 1,
                        );
                        const selectedSliced = filteredTemp.slice(0, index + 1);
                        return isEqual(comboSliced, selectedSliced);
                      });

                      return (
                        <li key={value.id}>
                          {value.image.path ? (
                            <WxRadioColor
                              label={value.label}
                              onSelect={() => {
                                const newTemp = temp.map((t, i) => {
                                  if (i > index) {
                                    setVariantSelected(-1);
                                    return -1;
                                  } else {
                                    return temp[i];
                                  }
                                });

                                setSelected(newTemp);
                                setLastSelectedIndex(index);
                              }}
                              disabled={
                                combArrayToTest.findIndex((comb) => {
                                  return (
                                    comb.attributes &&
                                    comb.attributes[index] === value.id
                                  );
                                }) === -1
                              }
                              selected={selected.includes(value.id)}
                              key={`attr-value-${value.id}`}
                            >
                              <WImage image={value.image} />
                            </WxRadioColor>
                          ) : (
                            <WxSelect
                              key={`attr-value-${value.id}`}
                              label={value.label}
                              disabled={
                                selected.filter((i) => i !== -1).length > index
                              }
                              onSelect={() => {
                                setSelected(temp);
                                setLastSelectedIndex(index);
                              }}
                              selected={selected.includes(value.id)}
                              darkMode
                            />
                          )}
                        </li>
                      );
                    })}
                  {key.values.find((e) => e.image.path) && (
                    <WxText
                      text={
                        key.values.find((value) => selected.includes(value.id))
                          ?.label ?? ''
                      }
                      darkMode
                    />
                  )}
                </UlValues>
              </Li>
            );
          })}
        </ul>
        <Hidden visible={selected.filter((i) => i !== -1).length > 0}>
          <WxButton
            darkMode
            label={__('Delete all filters')}
            type={'neutral'}
            noArrow
            onClick={() => {
              setSelected(attributesByKeys.map((_) => -1));
              setLastSelectedIndex(-1);
              setVariantSelected(-1);
            }}
          />
        </Hidden>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  @media (min-width: ${breakpoint}) {
    margin-bottom: 2.5rem;
  }
`;

const UlValues = styled.ul`
  display: flex;
  gap: 0.625rem;
  align-items: center;

  > div {
    position: relative;
  }
`;

const Li = styled.li<{ $hidden: boolean }>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'block')};

  > :first-child {
    margin-bottom: 0.625rem;
  }

  :last-child {
    margin-bottom: 0rem;
  }

  @media (min-width: ${breakpoint}) {
    :last-child {
      margin-bottom: 1.25rem;
    }
  }
  margin-bottom: 1.25rem;
`;

export const Hidden = styled.div<{ visible: boolean }>`
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
`;
